/**
 * NdfMobileSigningDrawer
 *
 * Slide-up bottom sheet for the NDF signing page on mobile. Reveals the
 * signing details (signer info, progress, per-field status) that live in the
 * desktop sidebar, so the document itself can stay full-screen behind the
 * compact MobileSigningBar.
 *
 * Mirrors MobileSigningDrawer (used by the legacy PDF page) but renders
 * NDF-shaped, read-only details — NDF fields are signed inline in the document.
 */
import { FileText } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetDescription,
} from '@respectly/ui';
import {
  NdfSigningDetails,
  type NdfSigningDetailsProps,
} from './NdfSigningDetails';

interface NdfMobileSigningDrawerProps extends NdfSigningDetailsProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  documentName: string;
  pageCount?: number;
}

export function NdfMobileSigningDrawer({
  open,
  onOpenChange,
  documentName,
  pageCount,
  ...details
}: NdfMobileSigningDrawerProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" showCloseButton={false} maxHeight="88vh">
        <div className="flex flex-shrink-0 flex-col items-center pt-2 pb-1">
          <div className="h-1 w-10 rounded-full bg-stone-300" aria-hidden />
        </div>

        <div className="flex flex-shrink-0 items-center gap-3 border-b border-stone-100 px-5 py-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-50">
            <FileText className="h-5 w-5 text-primary-600" />
          </div>
          <div className="min-w-0 flex-1">
            <SheetTitle className="truncate text-base text-stone-900">
              {documentName}
            </SheetTitle>
            {pageCount ? (
              <SheetDescription className="text-xs text-stone-500">
                {pageCount} page{pageCount > 1 ? 's' : ''}
              </SheetDescription>
            ) : (
              <SheetDescription className="text-xs text-stone-500">
                Review and add your signature
              </SheetDescription>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          <NdfSigningDetails {...details} />
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default NdfMobileSigningDrawer;
